import { getModel } from '../../model/registry'
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useQueryClient, useQueries } from "@tanstack/react-query";

import ModalForm from "./ModalForm";
import { useStore, FormStateProvider } from './useModalCardStore';
import generateEndpoint from "../../utils/generateEndpoint";
import useApi from "../../hooks/useApi";
import transferSettings, { getSettingsKey } from "../pageView/processSettings";
import SkeletonLoader from "../generic/SkeletonLoader";

const resolveQueryKey = (array, data) => {
    if (Array.isArray(array)) return array.map(part =>
        typeof part === 'string' && part.startsWith(':') && data?.[part.slice(1)] !== undefined
            ? data[part.slice(1)]
            : part
    );
    return ''
}

/**
 * The host for a record-editing modal.
 *
 * Since 33.1 tier 3 this renders no modal of its own — `ModalForm` draws it, the
 * `cardSection` widget draws the card, and `submit: { to: 'entity' }` sends the
 * answer to the server. What is left is the two jobs that are genuinely this
 * component's and not the form's:
 *
 *   1. **a store of its own.** A card modal edits a different record from the
 *      page behind it, so it cannot write into the page's store the way a form
 *      modal does. That is the whole reason this wrapper exists.
 *   2. **loading.** `load` fetches the record before the form is shown. Whether
 *      to load is the caller's business, not the model's: a connect field's edit
 *      pencil opens an existing record, kanban's "+" starts an empty one.
 *
 * It was 283 lines across two files. The POST/PUT moved to `useModalSubmit`, the
 * rendering to `ModalForm`, and the header, footer and key handling turned out
 * to be a second copy of what `ModalForm` already had.
 */
const ModalCardBody = ({
    data = {},
    mode = '',
    options,
    testId,
    entity,
    origin,
    onClose,
    zIndex,
    load,
    effect,
    opacity = 0,
    defaultValues
}) => {
    const [loaded, setLoaded] = useState(false)

    const [, setState] = useStore((s) => s)
    const { getMe } = useApi()
    const queryClient = useQueryClient();
    const getSettingsData = (key) => queryClient.getQueryData([key])?.data

    const config = getModel('modalCardData')[entity]
    const queryArray = resolveQueryKey(config?.queryArray || [], origin)

    const settingsKeys = config?.loadSettings || []
    const settingsQueries = useQueries({
        queries: settingsKeys.map(entry => {
            const key = getSettingsKey(entry)
            return { queryKey: [key], queryFn: () => getMe(key), staleTime: Infinity }
        })
    })
    const settingsReady = settingsKeys.length === 0 || settingsQueries.every(q => q.isSuccess)

    useEffect(() => {
        if (!settingsReady) return
        if (data?.id) {
            const enriched = settingsKeys.length > 0
                ? transferSettings(data, getSettingsData, settingsKeys)
                : data
            setState(enriched)
            queryClient.refetchQueries(queryArray)
            setLoaded(true)
        } else if (defaultValues) {
            const enriched = settingsKeys.length > 0
                ? transferSettings(defaultValues, getSettingsData, settingsKeys)
                : defaultValues
            setState(enriched)
        }
    }, [data, settingsReady])

    if (!config) {
        console.warn(`[ModalCard] no modalCardData entry for entity '${entity}'`)
        return null
    }

    return ReactDOM.createPortal(
        <div>
            {(!load || loaded)
                ? <ModalForm
                    isVisible={true}
                    setIsVisible={() => { }}
                    modalTitle={load ? config.titleBar?.edit : config.titleBar?.new}
                    id={config.id}
                    groups={config.elements}
                    buttons={config.buttons}
                    submit={config.submit}
                    submitPath={config.path}
                    queryArray={queryArray}
                    recordId={data?.id}
                    validateOnSubmit={true}
                    entity={entity}
                    useStore={useStore}
                    options={options}
                    parentId={origin?.id}
                    mode={mode}
                    opacity={opacity}
                    minHeight='38rem'
                    zIndex={zIndex}
                    testId={testId}
                    onClose={onClose}
                    onDone={effect}
                />
                : <SkeletonLoader config={['L', 'F', 'L', 'F', 'F']} />}
        </div>
        , document.body);
};

const ModalCard = (props) => {
    const { isVisible, entity, origin, id, load } = props
    const { getMe } = useApi()
    const [data, setData] = useState({})

    const config = getModel('modalCardData')[entity]
    const endpoint = generateEndpoint(config?.path || '', origin?.id)

    useEffect(() => {
        if (!load) return
        getMe(`${endpoint}/${id}`).then(result => setData(result.data))
    }, [])

    return (
        <FormStateProvider>
            {isVisible && <ModalCardBody {...props} data={data} />}
        </FormStateProvider>
    )
}

export default ModalCard
